import { jsPDF } from "jspdf";
import { fmtNum, formatDMY } from "./finance";
import { LEAN_LOGO_DATA_URL } from "../assets/leanLogo";

// Generates and downloads a Tax Invoice PDF, ported from the original portal layout.
export function generateInvoicePdf(record, businessProfile) {
  const biz = businessProfile || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 42;
  let y = 50;

  // Brand logo (top-left)
  try {
    const logoW = 96, logoH = 76; // 616x488 aspect ratio preserved
    doc.addImage(LEAN_LOGO_DATA_URL, "PNG", margin, 34, logoW, logoH);
  } catch (e) { console.warn("Invoice logo could not be embedded; continuing without it.", e); }

  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120);
  doc.text("Page 1 of 1", pageWidth - margin, y - 20, { align: "right" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(20);
  doc.text("TAX INVOICE", pageWidth - margin, y, { align: "right" });

  y += 20;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(biz.name || "", pageWidth - margin, y, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(80);
  y += 13;
  doc.text(biz.udyam || "", pageWidth - margin, y, { align: "right" });
  (biz.address || "").split("\n").forEach((line) => { y += 13; doc.text(line, pageWidth - margin, y, { align: "right" }); });

  y += 16;
  doc.setFontSize(9); doc.setTextColor(40);
  const metaColX = pageWidth - margin - 320;
  doc.text("Invoice date:", metaColX, y); doc.text(formatDMY(record.date), metaColX + 85, y);
  doc.text("Invoice no:", metaColX + 170, y); doc.text(String(record.number), metaColX + 250, y);
  y += 14;
  doc.text("Due date:", metaColX, y); doc.text(record.dueDate ? formatDMY(record.dueDate) : "-", metaColX + 85, y);

  y = 130;
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(20);
  doc.text(record.billTo || "", margin, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(80);
  y += 16;
  (record.billToAddress || "").split("\n").filter(Boolean).forEach((line) => { doc.text(line, margin, y); y += 13; });
  if (record.billToGst) { doc.text("GST : " + record.billToGst, margin, y); y += 13; }

  y += 20;
  doc.setDrawColor(220); doc.setLineWidth(0.5); doc.line(margin, y, pageWidth - margin, y);
  y += 14;
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(20);
  doc.text("DESCRIPTION", margin, y);
  doc.text("PRICE", pageWidth - margin - 220, y, { align: "right" });
  doc.text("QTY", pageWidth - margin - 170, y, { align: "right" });
  doc.text("DISCOUNT", pageWidth - margin - 110, y, { align: "right" });
  doc.text("IGST", pageWidth - margin - 55, y, { align: "right" });
  doc.text("AMOUNT", pageWidth - margin, y, { align: "right" });
  y += 8; doc.line(margin, y, pageWidth - margin, y); y += 16;

  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(30);
  let basis = 0, discountTotal = 0, igstTotal = 0;
  (record.items || []).forEach((item) => {
    const amt = item.price * item.qty - item.discount;
    basis += item.price * item.qty; discountTotal += item.discount; igstTotal += amt * (item.igstRate / 100);
    doc.text(item.description || "", margin, y);
    doc.text(fmtNum(item.price), pageWidth - margin - 220, y, { align: "right" });
    doc.text(String(item.qty), pageWidth - margin - 170, y, { align: "right" });
    doc.text(item.discount ? fmtNum(item.discount) : "-", pageWidth - margin - 110, y, { align: "right" });
    doc.text(item.igstRate ? item.igstRate + "%" : "-", pageWidth - margin - 55, y, { align: "right" });
    doc.text(fmtNum(amt), pageWidth - margin, y, { align: "right" });
    y += 18;
  });
  const netAmount = basis - discountTotal;
  const total = netAmount + igstTotal;

  y += 8; doc.line(margin, y, pageWidth - margin, y); y += 22;
  const totalsX = pageWidth - margin - 160;
  doc.setFontSize(9.5); doc.setTextColor(60);
  doc.text("Net amount", totalsX, y); doc.text(fmtNum(netAmount), pageWidth - margin, y, { align: "right" });
  y += 15;
  doc.text("IGST", totalsX, y); doc.text(igstTotal ? fmtNum(igstTotal) : "-", pageWidth - margin, y, { align: "right" });
  y += 15;
  doc.text("Discount", totalsX, y); doc.text(discountTotal ? fmtNum(discountTotal) : "-", pageWidth - margin, y, { align: "right" });
  y += 18;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(20);
  doc.text("TOTAL DUE (INR)", totalsX, y); doc.text(fmtNum(total), pageWidth - margin, y, { align: "right" });

  const footerY = 760;
  doc.setFillColor(142, 221, 163); doc.rect(margin, footerY - 14, pageWidth - 2 * margin, 3, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(20);
  doc.text("PAYMENT INFORMATION", margin, footerY);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(60);
  doc.text("Invoice number:", margin, footerY + 16); doc.text(String(record.number), margin + 90, footerY + 16);
  doc.text("Amount (INR):", margin, footerY + 30); doc.text(fmtNum(total), margin + 90, footerY + 30);

  const bankColX = pageWidth - margin - 280;
  [["Bank account name:", biz.bankAccountName || ""], ["Bank account number:", biz.bankAccountNumber || ""], ["Bank name:", biz.bankName || ""], ["IFSC:", biz.ifsc || ""]].forEach((pair, i) => {
    doc.text(pair[0], bankColX, footerY + 16 + i * 13);
    doc.text(pair[1], bankColX + 140, footerY + 16 + i * 13);
  });

  doc.setDrawColor(220); doc.line(margin, 812, pageWidth - margin, 812);
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(100);
  doc.text(`${(biz.name || "").toUpperCase()}  ${biz.udyam || ""}`, margin, 826);
  doc.setFont("helvetica", "normal");
  doc.text(`${biz.email || ""}   ${biz.phone || ""}`, pageWidth - margin, 826, { align: "right" });

  doc.save(`Invoice_${record.number}_${(record.billTo || "client").replace(/[^a-z0-9]/gi, "_")}.pdf`);
  return total;
}
